<template>
  <div>
    <div
      v-if="result" :key="result" class="calculation-steps"
      :class="{ result: result }"
    >
      <h2 class="h4">
        Calculation Example:
      </h2>
      <p>Performing a simple linear regression analysis is easy with our regression line calculator. Simply input your data, and it generates the results for you.</p>
      <p class="small-margins">
        And if you wish to perform the calculations manually, follow our step-by-step instructions.
      </p>
      <ol class="ol">
        <li>
          <p>Calculate the mean of the predictor variable (x):</p>
          <p class="formulas">
            \[\bar{x}=\frac{\sum_{i=1}^{n}x_i}{n}=\frac{ {{ additionString[0] }} }{ {{ calculationData.inputArrays[0].length }} }={{ results.xMean }}\]
          </p>
        </li>
        <li class="small-margins">
          <p>Calculate the mean of the dependent variable (y):</p>
          <p class="formulas">
            \[\bar{y}=\frac{\sum_{i=1}^{n}y_i}{n}=\frac{ {{ additionString[1] }} }{ {{ calculationData.inputArrays[1].length }} }={{ results.yMean }}\]
          </p>
        </li>
        <li class="small-margins">
          <p>Subtract the mean of the predictor variable from each independent variable. We have the following equation for the first variable:</p>
          <p class="formulas">
            \[(x_i-\bar{x})={{ `${calculationData.inputArrays[0][0]} - ${results.xMean}` }}={{ results.independent }}\]
          </p>
        </li>
        <li>
          <p>Subtract the mean of the dependent variable from each dependent variable. We have the following equation for the first variable:</p>
          <p class="formulas">
            \[(y_i-\bar{y})={{ `${calculationData.inputArrays[1][0]} - ${results.yMean}` }}={{ results.dependant }}\]
          </p>
        </li>
        <li>
          <p>Calculate the sum of products (SP) of deviations of the predictor and dependent variable:</p>
          <p class="formulas">
            \[S_{xy}=\sum_{i=1}^{n}{(x_i-\bar{x})(y_i-\bar{y})={{ sumOfProductsString }}={{ results.sumOfProducts }}}\]
          </p>
        </li>
        <li class="small-margins">
          <p>Calculate each independent variable’s squared deviation from the mean. We have the following equation for the first variable:</p>
          <p class="formulas">
            \[{(x_i-\bar{x})}^2={({{ `${calculationData.inputArrays[0][0]} - ${results.xMean}` }})={{ results.independent }}}^2={{ results.squaredDeviation }}\]
          </p>
        </li>
        <li class="small-margins">
          <p>Calculate the sum of all the squared differences to obtain the sum of squares <span class="inline-formulas">\[({SS}_x)\]</span> of the independent variable:</p>
          <p class="formulas">
            \[{SS}_x=\sum_{i=1}^{n}{(x_i-\bar{x})^2={{ independentSumSquaredDif }}={{ results.squaredDeviationsOfX }}}\]
          </p>
        </li>
        <li class="small-margins">
          <p>Divide the sum of products (SP) of deviations of the predictor and dependent variable <span class="inline-formulas">\[(S_{xy})\]</span> by the sum of squares of the independent variable <span class="inline-formulas">\[({SS}_x)\]</span> to obtain the slope of the regression line <span class="inline-formulas">\[{(b}_1)\]</span>:</p>
          <p class="formulas">
            \[b_1=\frac{\sum_{i=1}^{n}(x_i-\bar{x})(y_i-\bar{y})}{\sum_{i=1}^{n}(x_i-\bar{x})^2}=\frac{ {{ results.sumOfProducts }} }{ {{ results.squaredDeviationsOfX }} }={{ results.slope }}\]
          </p>
        </li>
        <li class="small-margins">
          <p>Multiply the mean of the predictor variable <span class="inline-formulas">\[\bar{x}\]</span> and the slope of the regression line <span class="inline-formulas">\[{(b}_1)\]</span>. Subtract the result from the mean of the dependent variable <span class="inline-formulas">\[\bar{y}\]</span> to obtain the intercept of the regression line:</p>
          <p class="formulas">
            \[b_0=\bar{y}-b_1\bar{x}={{ results.yMean }}-{{ results.slope }}\times{{ results.xMean }}={{ results.intercept }}\]
          </p>
        </li>
        <li class="small-margins">
          <p>In the end, we obtain the regression equation:</p>
          <p class="formulas">
            \[\hat{y}=b_0+b_1x_1\]
          </p>
          <p class="formulas">
            \[\hat{y}={{ results.intercept }}+{{ results.slope }}x_1\]
          </p>
        </li>
      </ol>

      <div v-if="result" class="python-r-codes python">
        <span class="img-text"><img alt="Python icon" src="../../../../assets/images/icon-python-calc.png"> <h4 class="h6">Your Result in Python:</h4></span>
        <p class="codes-info">
          The following Python code should provide linear regression using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-python"
            ><code id="python"># Import relevant libraries

import numpy as np

import pandas as pd

import matplotlib.pyplot as plt

import statsmodels.api as sm

# Input data

X = np.array(<span class="no-wrap">{{ inputToNumbers[0][0] }}</span>)

Y = np.array(<span class="no-wrap">{{ inputToNumbers[0][1] }}</span>)

# Regression

X = sm.add_constant(X)

model = sm.OLS(Y, X).fit()

# Output

print(f'Regression equation: Y = {model.params[0]:.2f} + {model.params[1]:.2f}X')

print(model.summary())

# Scatter plot with regression line

plt.scatter(X[:, 1], Y)

plt.plot(X[:, 1], model.predict(), color='red')

plt.title('Linear Regression')

plt.xlabel('X')

plt.ylabel('Y')

plt.show()</code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedPython === 'Copied' }" @click="copyCode('python')">
          {{ copyCodeOrCopiedPython }}
        </button>
      </div>
      <div v-if="result" class="python-r-codes">
        <span class="img-text"><img alt="Icon for R language" src="../../../../assets/images/icon-r-calc.png"> <h4 class="h6">Your Result in R:</h4></span>
        <p class="codes-info">
          The following R code should provide linear regression using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-r"
            ><code id="r"># Input data

x &lt;- c(<span class="no-wrap">{{ inputToNumbers[1][0] }}</span>)

y &lt;- c(<span class="no-wrap">{{ inputToNumbers[1][1] }}</span>)

# Linear regression model

model &lt;- lm(y ~ x)

# Regression summary

summary(model)

# ANOVA table

anova(model)

# Scatter plot with regression line

plot(x, y, main = "Scatter Plot with Regression Line")

abline(model, col = "red")</code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedR === 'Copied' }" @click="copyCode('r')">
          {{ copyCodeOrCopiedR }}
        </button>
      </div>
    </div>
    <div v-else class="calculation-steps">
      <h3 class="h4">
        Linear Regression Equation: Step-by-Step Calculations
      </h3>
      <p class="bottom-margin-20">
        Performing a simple linear regression analysis is easy with our regression line calculator. Simply input your data, and it generates the results for you.
      </p>
      <p class="bottom-margin-40">
        And if you wish to perform the calculations manually, follow our step-by-step instructions.
      </p>
      <ol class="steps-ol">
        <li>
          <p>Calculate the mean of the predictor variable (x):</p>
          <p class="formulas">
            \[\bar{x}=\frac{\sum_{i=1}^{n}x_i}{n}\]
          </p>
        </li>
        <li>
          <p>Calculate the mean of the dependent variable (y):</p>
          <p class="formulas">
            \[\bar{y}=\frac{\sum_{i=1}^{n}y_i}{n}\]
          </p>
        </li>
        <li>
          <p>Subtract the mean of the predictor variable from each independent variable:</p>
          <p class="formulas">
            \[(x_i-\bar{x})\]
          </p>
        </li>
        <li>
          <p>Subtract the mean of the dependent variable from each dependent variable:</p>
          <p class="formulas">
            \[(y_i-\bar{y})\]
          </p>
        </li>
        <li>
          <p>Calculate the sum of products (SP) of deviations of the predictor and dependent variable:</p>
          <p class="formulas">
            \[S_{xy}=\sum_{i=1}^{n}{(x_i-\bar{x})(y_i-\bar{y})}\]
          </p>
        </li>
        <li>
          <p>Calculate each independent variable’s squared deviation from the mean:</p>
          <p class="formulas">
            \[{(x_i-\bar{x})}^2\]
          </p>
        </li>
        <li>
          <p>Calculate the sum of all the squared differences to obtain the sum of squares (<span class="inline-formulas">\[SS_x\]</span>) of the independent variable:</p>
          <p class="formulas">
            \[{SS}_x=\sum_{i=1}^{n}{(x_i-\bar{x})}^2\]
          </p>
        </li>
        <li>
          <p> Divide the sum of products (SP) of deviations of the predictor and dependent variable (<span class="inline-formulas">\[S_{xy}\]</span>) by the sum of squares of the independent variable (<span class="inline-formulas">\[{SS}_x\]</span>) to obtain the slope of the regression line (<span class="inline-formulas">\[b_1\]</span>):</p>
          <p class="formulas">
            \[S_{xy}=\sum_{i=1}^{n}(x_i-\bar{x})(y_i-\bar{y})\]
          </p>
        </li>
        <li>
          <p>Multiply the mean of the predictor variable <span class="inline-formulas">\[\bar{x}\]</span> and the slope of the regression line (<span class="inline-formulas">\[b_1\]</span>). Subtract the result from the mean of the dependent variable <span class="inline-formulas">\[\bar{y}\]</span> to obtain the intercept of the regression line:</p>
          <p class="formulas">
            \[b_0=\bar{y}-b_1\bar{x}\]
          </p>
        </li>
      </ol>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  data() {
    return {
      copyCodeOrCopiedPython: 'Copy Code',
      copyCodeOrCopiedR: 'Copy Code',
      results: {},
    };
  },
  computed: {
    ...mapState('calculators', ['result', 'calculationData']),
    additionString() {
      const { length } = this.calculationData.inputArrays[0];
      const additionsArrays = this.calculationData.inputArrays.map((arr) => arr.map((num, i) => (length - 1 > i ? `${num} + ` : num)));

      return additionsArrays.map((arr) => arr.join(''));
    },
    sumOfProductsString() {
      const { length } = this.calculationData.inputArrays[0];
      const resultArr = [];

      for (let i = 0; i < length; i += 1) {
        const currentElement = length - 1 > i
          ? `(${this.calculationData.inputArrays[0][i]} - ${this.results.xMean})(${this.calculationData.inputArrays[1][i]} - ${this.results.yMean}) + `
          : `(${this.calculationData.inputArrays[0][i]} - ${this.results.xMean})(${this.calculationData.inputArrays[1][i]} - ${this.results.yMean})`;

        resultArr.push(currentElement);
      }

      return resultArr.join('');
    },
    independentSumSquaredDif() {
      const { length } = this.calculationData.inputArrays[0];
      const resultArr = [];

      for (let i = 0; i < length; i += 1) {
        const currentElement = length - 1 > i
          ? `(${this.calculationData.inputArrays[0][i]} - ${this.results.xMean})^2 + `
          : `(${this.calculationData.inputArrays[0][i]} - ${this.results.xMean})^2`;

        resultArr.push(currentElement);
      }

      return resultArr.join('');
    },
    inputToNumbers() {
      const inputArrays = this.calculationData.inputArrays.map((arr) => arr.map((str) => Number(str)));
      return [inputArrays, inputArrays.map((arr) => arr.join(', '))];
    },
  },
  watch: {
    result() {
      if (this.result) {
        this.calculations();
      }
    },
  },
  methods: {
    copyCode(id) {
      if (id.split('-')[0] === 'python') {
        this.copyCodeOrCopiedPython = 'Copied';
        setTimeout(() => {
          this.copyCodeOrCopiedPython = 'Copy Code';
        }, 1000);
      } else {
        this.copyCodeOrCopiedR = 'Copied';
        setTimeout(() => {
          this.copyCodeOrCopiedR = 'Copy Code';
        }, 1000);
      }

      const currentText = document.getElementById(id).innerText;
      this.$copy(currentText);
    },
    calculations() {
      const results = {
        independent: (this.calculationData.inputArrays[0][0] - this.calculationData.meansArray[0]),
        dependant: (this.calculationData.inputArrays[1][0] - this.calculationData.meansArray[1]),
        xMean: (this.calculationData.meansArray[0]),
        yMean: (this.calculationData.meansArray[1]),
        sumOfProducts: (this.calculationData.sumOfProducts),
        squaredDeviation: ((this.calculationData.inputArrays[0][0] - this.calculationData.meansArray[0]) ** 2),
        squaredDeviationsOfX: (this.calculationData.squaredDeviationsOfX),
        slope: (this.calculationData.slope),
        intercept: (this.calculationData.intercept),
      };

      this.checkAndFormatResult(results);
    },
    checkAndFormatResult(obj) {
      const resultsArr = Object.entries(obj);
      const onlyZerosTest = /^\d+.[0]+$/;

      for (let i = 0; i < resultsArr.length; i += 1) {
        if (resultsArr[i][1].toString().split('').includes('e')) {
          let numberPart = 0;
          let additionalPart = '';

          [numberPart, additionalPart] = resultsArr[i][1].toString().split(/(?=e)/);
          numberPart = Number(numberPart);

          if (onlyZerosTest.test(numberPart)) {
            numberPart = Math.trunc(numberPart);
          } else {
            numberPart = numberPart.toFixed(this.selectedDecimalPlaces);
          }

          this.results[resultsArr[i][0]] = numberPart.toString().concat(additionalPart);
        }

        if (resultsArr[i][1] % 1 === 0 || typeof resultsArr[i][1] === 'string') {
          [, this.results[resultsArr[i][0]]] = resultsArr[i];
        } else if (onlyZerosTest.test(resultsArr[i][1])) {
          resultsArr[i] = Math.trunc(resultsArr[i][1]);
          [, this.results[resultsArr[i][0]]] = resultsArr[i];
        } else {
          this.results[resultsArr[i][0]] = resultsArr[i][1].toFixed(this.selectedDecimalPlaces);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.calculation-steps {
  p {
    margin: 0;
  }

  .python-r-codes.python {
    margin-top: var(--spacing-80);
  }

  ul {
    padding-left: 0;
  }

  .ol {
    margin-top: var(--spacing-40);
  }

  .bottom-margin-40 {
    margin-bottom: var(--spacing-40);
  }

  .bottom-margin-20 {
    margin-bottom: var(--spacing-20);
  }
}

@include bp($bp-mobile) {
  div .calculation-steps {
    padding-top: var(--spacing-40);

    .h4 {
      text-align: left;
    }
  }
}

@import '../../../../assets/sass/ds/calculators/calculation-steps.scss';
</style>
